import React from 'react'
import { RADIO_OPTIONS, PARTIALLY_APPROVED_OPTION } from './radioOptions'
import {
  getRadioButtonState,
  shouldHidePartiallyApprovedOption,
} from './radioButtonUtils'
import { ButtonStateConfig } from './buttonStateUtils'

interface StatusRadioOptionsProps {
  selectedStatus: string
  onStatusChange: (status: string) => void
  buttonStateConfig?: ButtonStateConfig
  canHold: boolean
  isRetracted: boolean
  numberOfDates: number
  isDenied?: boolean
  isReturnedAllowed?: boolean
  disableApproved?: boolean
  /** When true, all radio buttons are disabled (e.g., History Tab with past dates) */
  disableAllOptions?: boolean
}

const StatusRadioOptions: React.FC<StatusRadioOptionsProps> = ({
  selectedStatus,
  onStatusChange,
  buttonStateConfig,
  canHold,
  isRetracted,
  numberOfDates,
  isDenied = false,
  isReturnedAllowed = true,
  disableApproved = false,
  disableAllOptions = false,
}) => {
  const renderRadioOption = (option: (typeof RADIO_OPTIONS)[number]) => {
    const { isDisabled: configDisabled, labelColor: configLabelColor } = getRadioButtonState(
      option.value,
      buttonStateConfig,
      { canHold, isRetracted }
    )
    const isDisabled =
      disableAllOptions ||
      configDisabled ||
      (option.value === 'approved' && disableApproved) ||
      (option.value === 'denied' && isDenied) ||
      (option.value === 'hold' && isDenied) ||
      (option.value === 'returned' && !isReturnedAllowed)
    const labelColor = isDisabled ? 'text-[#A0A5B6]' : configLabelColor

    return (
      <div key={option.id} className="flex items-center gap-3 mb-1!">
        <input
          type="radio"
          id={option.id}
          name="status"
          value={option.value}
          checked={selectedStatus === option.value}
          onChange={(e) => onStatusChange(e.target.value)}
          disabled={isDisabled}
          className="w-4 h-4"
        />

        <label
          htmlFor={option.id}
          className={`text-base font-light leading-6 tracking-[0.16px] cursor-pointer ${labelColor}`}
        >
          {option.label}
        </label>
      </div>
    )
  }

  const {
    isDisabled: configPartialDisabled,
    labelColor: configPartialLabelColor,
  } = getRadioButtonState(PARTIALLY_APPROVED_OPTION.value, buttonStateConfig, {
    canHold,
    isRetracted,
  })
  const partiallyApprovedDisabled = disableAllOptions || configPartialDisabled || isDenied
  const partiallyApprovedLabelColor = partiallyApprovedDisabled
    ? 'text-[#A0A5B6]'
    : configPartialLabelColor

  return (
    <>
      {RADIO_OPTIONS.map(renderRadioOption)}

      {!shouldHidePartiallyApprovedOption(isRetracted, numberOfDates) && (
        <div className="flex items-center gap-3">
          <input
            type="radio"
            id={PARTIALLY_APPROVED_OPTION.id}
            name="status"
            value={PARTIALLY_APPROVED_OPTION.value}
            checked={selectedStatus === PARTIALLY_APPROVED_OPTION.value}
            onChange={(e) => onStatusChange(e.target.value)}
            disabled={partiallyApprovedDisabled}
            className="w-4 h-4"
          />

          <label
            htmlFor={PARTIALLY_APPROVED_OPTION.id}
            className={`text-base font-light leading-6 tracking-[0.16px] cursor-pointer ${partiallyApprovedLabelColor}`}
          >
            {PARTIALLY_APPROVED_OPTION.label}
          </label>
        </div>
      )}
    </>
  )
}

export default StatusRadioOptions
